/**
 * EA Diagram Store
 *
 * Canister client wrapper for EA diagram CRUD operations.
 * Communicates with vector_db canister to store/retrieve AI-generated SVGs.
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only diagram persistence operations
 * - Repository Pattern: Abstracts canister storage details
 */

import type { CanisterClient } from '../canister-client';

interface CandidResult<T> {
    Ok?: T;
    Err?: string;
}

/**
 * Repository class for EA diagram storage in vector_db canister.
 */
export class DiagramStore {
    private canisterClient: CanisterClient;

    constructor(canisterClient: CanisterClient) {
        this.canisterClient = canisterClient;
    }

    /**
     * Store an AI-generated SVG diagram for a specific EA tab.
     * Overwrites any existing diagram for the same tab.
     */
    async store(tabName: string, svg: string, tokenCount: number = 0): Promise<void> {
        const result = await this.canisterClient.callCanister<CandidResult<string>>(
            'vector_db',
            'store_ea_diagram',
            [tabName, svg, tokenCount]
        );
        if (result?.Err) {
            throw new Error(result.Err);
        }
    }

    /**
     * Retrieve the stored SVG diagram for an EA tab.
     * Returns null if no diagram exists for this tab.
     */
    async get(tabName: string): Promise<string | null> {
        const result = await this.canisterClient.callCanister<string[] | null>(
            'vector_db',
            'get_ea_diagram',
            [tabName]
        );
        // Candid opt returns [] for None, [value] for Some
        if (Array.isArray(result) && result.length > 0) {
            return result[0];
        }
        return null;
    }

    /**
     * Clear all stored EA diagrams.
     * Returns the number of diagrams removed.
     */
    async clearAll(): Promise<number> {
        return await this.canisterClient.callCanister<number>(
            'vector_db',
            'clear_ea_diagrams',
            []
        );
    }
}
